import { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { User } from 'firebase/auth';
import { Clock } from 'lucide-react';
import AppHeader from './AppHeader';

type DashboardLayoutProps = {
  isDarkMode: boolean;
  onToggleTheme: () => void;
  currentUser: User | null;
  onSignIn: () => void;
  onCreateAccount: () => void;
  onProfile?: () => void;
  onLogout?: () => void;
  needsOrganizationSetup?: boolean;
  hasOrganizationAccess?: boolean;
  organizationMembershipStatus?: string;
  extraNavItems?: NavItem[];
  hideNavigation?: boolean;
  children: ReactNode;
};

type NavItem = {
  label: string;
  to: string;
};

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/dashboard' },
  { label: 'Organization', to: '/organization' },
  { label: 'Locations', to: '/locations' },
  { label: 'Billing', to: '/billing' },
  { label: 'Team', to: '/users' },
];

export default function DashboardLayout({
  isDarkMode,
  onToggleTheme,
  currentUser,
  onSignIn,
  onCreateAccount,
  onProfile,
  onLogout,
  needsOrganizationSetup,
  hasOrganizationAccess,
  organizationMembershipStatus,
  extraNavItems,
  hideNavigation,
  children,
}: DashboardLayoutProps) {
  const location = useLocation();
  const shellBackground = isDarkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-900';
  const containerClass = `${shellBackground} min-h-screen flex flex-col`;
  const sidebarClass = `${isDarkMode ? 'bg-slate-900/80 text-slate-100 ring-slate-800' : 'bg-white text-slate-800 ring-slate-200'} flex max-h-[calc(100vh-6rem)] w-full flex-col gap-2 rounded-lg px-4 py-6 ring-1`;
  const mainCardClass = `${isDarkMode ? 'text-slate-100' : 'text-slate-900'} min-w-0 flex-1`;
  const isPending = organizationMembershipStatus === 'Pending';
  const showDashboardLink = Boolean(hasOrganizationAccess) && !isPending;
  const baseNavItems = needsOrganizationSetup
    ? NAV_ITEMS.filter(
        item => item.to !== '/dashboard' && item.to !== '/users' && item.to !== '/locations' && item.to !== '/billing',
      )
    : NAV_ITEMS;
  const navItems = [...baseNavItems];
  if (extraNavItems?.length) {
    for (const item of extraNavItems) {
      if (!navItems.some(existing => existing.to === item.to)) {
        navItems.push(item);
      }
    }
  }

  if (isPending) {
    const pendingCardClass = `${isDarkMode ? 'border-slate-700 bg-slate-900/70 text-slate-100 shadow-slate-900/40' : 'border-slate-200 bg-white text-slate-900 shadow-slate-200/70'} flex w-full max-w-xl flex-col items-center gap-6 rounded-3xl border px-8 py-16 text-center shadow-lg`;
    return (
      <div className={containerClass}>
        <AppHeader
          isDarkMode={isDarkMode}
          onToggleTheme={onToggleTheme}
          currentUser={currentUser}
          onSignIn={onSignIn}
          onCreateAccount={onCreateAccount}
          onProfile={onProfile}
          onLogout={onLogout}
          showDashboardLink={showDashboardLink}
        />
        <div className="flex flex-1 items-center justify-center px-4 pb-20 pt-10 sm:px-6 lg:px-8">
          <div className={pendingCardClass}>
            <Clock className="h-16 w-16 text-blue-500" aria-hidden="true" />
            <p className="text-2xl font-semibold">Your access request is pending.</p>
            <p className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'} text-sm leading-relaxed`}>
              Check back later or reach out to your clinic&apos;s administrator if you need the request expedited.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={containerClass}>
      <AppHeader
        isDarkMode={isDarkMode}
        onToggleTheme={onToggleTheme}
        currentUser={currentUser}
        onSignIn={onSignIn}
        onCreateAccount={onCreateAccount}
        onProfile={onProfile}
        onLogout={onLogout}
        showDashboardLink={showDashboardLink}
      />
      <div className={`mx-auto flex w-full flex-1 gap-8 px-4 pb-20 pt-10 sm:px-6 lg:px-8 ${hideNavigation ? 'max-w-7xl' : 'max-w-6xl'}`}>
        {!hideNavigation ? (
          <aside className="hidden w-64 flex-shrink-0 lg:block">
            <nav className={sidebarClass}>
              <h2 className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Navigation</h2>
              <ul className="space-y-1">
                {navItems.map(item => {
                  const isActive =
                    location.pathname === item.to ||
                    (item.to !== '/dashboard' && location.pathname.startsWith(item.to));
                  const baseClasses =
                    'flex items-center rounded-lg px-3 py-2 text-sm font-medium transition focus:outline-none focus:ring-2 focus:ring-offset-2';
                  const activeClasses = isActive
                    ? isDarkMode
                      ? 'bg-blue-500 text-white focus:ring-blue-400 focus:ring-offset-slate-900'
                      : 'bg-blue-600 text-white focus:ring-blue-300 focus:ring-offset-white'
                    : isDarkMode
                      ? 'text-slate-200 hover:bg-slate-800 focus:ring-slate-700 focus:ring-offset-slate-900'
                      : 'text-slate-600 hover:bg-slate-100 focus:ring-slate-300 focus:ring-offset-white';
                  return (
                    <li key={item.to}>
                      <Link to={item.to} className={`${baseClasses} ${activeClasses}`}>
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </aside>
        ) : null}
        <main className={mainCardClass}>
          {children}
        </main>
      </div>
    </div>
  );
}
